/**
 * =============================================================================
 *  localStorage 的安全包裝。
 *
 *  三種環境會讓 `localStorage` 不存在或一碰就丟：
 *   · vitest 的 node 環境（專案刻意不裝 jsdom）；
 *   · Safari 無痕模式（`setItem` 丟 QuotaExceededError）；
 *   · 使用者在瀏覽器設定裡封鎖了網站資料。
 *
 *  三種都**不是錯誤**：記不住偏好只是下次要重選一次，不該讓整個 app 白屏。
 *  所以每一次存取都包在 try/catch 裡，拿不到就退回一個什麼都不做的實作
 *  ——與 lib/i18n/index.ts 對語言偏好的處理是同一個姿態。
 * ========================================================================== */

interface SimpleStorage {
  getItem: (name: string) => string | null;
  setItem: (name: string, value: string) => void;
  removeItem: (name: string) => void;
}

const NOOP: SimpleStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

export function safeLocalStorage(): SimpleStorage {
  try {
    if (typeof localStorage === 'undefined') return NOOP;
    // 只有真的讀得到才算可用；某些環境裡物件存在但一存取就丟。
    localStorage.getItem('__probe__');
  } catch {
    return NOOP;
  }
  return {
    getItem: (name) => {
      try {
        return localStorage.getItem(name);
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      try {
        localStorage.setItem(name, value);
      } catch {
        // 存不下去（配額滿、無痕）——偏好記不住，畫面照常。
      }
    },
    removeItem: (name) => {
      try {
        localStorage.removeItem(name);
      } catch {
        // 同上。
      }
    },
  };
}
